import {
    Apartment,
    Badge,
    CalendarMonth,
    LocationOn,
    Store,
    Work,
} from '@mui/icons-material'
import { Container } from '@mui/material'
import DataPoint from 'recruiter/components/DataPoint'
import { getPxAsRem } from 'shared/utils'

import { useTheme } from '@emotion/react'
import type { GetRosterCandidateForClientProfileQuery } from '__generated__/graphql'
import type { FC } from 'react'
import IndustryOrSectorList from 'recruiter/components/IndustryOrSectorList'

interface CandidateDetailDataPointsProps {
    data: GetRosterCandidateForClientProfileQuery
}

const CandidateDetailDataPoints: FC<CandidateDetailDataPointsProps> = ({
    data,
}) => {
    const theme = useTheme()

    const currentCandidate = data.rosterCandidate?.candidate
    const currentWorkExperience = currentCandidate?.workExperience?.find(
        (workExperience) => workExperience.isCurrent
    )

    switch (currentWorkExperience?.title?.value) {
        case 'College Student':
        case 'Masters Student':
        case 'Student':
            return (
                <Container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: getPxAsRem(8),
                        flexWrap: 'wrap',
                        padding: getPxAsRem(8),
                    }}
                >
                    <DataPoint
                        value="College Student"
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                        title="Employment Type"
                    />
                    {!currentCandidate?.education?.[0] && (
                        <DataPoint
                            title="Start Year"
                            value={currentCandidate?.startYear ?? ''}
                            icon={
                                <CalendarMonth
                                    htmlColor={
                                        theme.palette.other.secondary_light
                                    }
                                />
                            }
                        />
                    )}
                    <DataPoint
                        title="Graduation Date"
                        value={
                            currentCandidate?.education?.[0]?.graduationDate ??
                            ''
                        }
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Container>
            )
        case 'MBA Student':
            return (
                <Container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: getPxAsRem(8),
                        padding: getPxAsRem(8),
                        flexWrap: 'wrap',
                    }}
                >
                    <DataPoint
                        value="MBA Student"
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                        title="Employment Type"
                    />
                    {!currentCandidate?.education?.[0] && (
                        <DataPoint
                            title="Start Year"
                            value={currentCandidate?.startYear ?? ''}
                            icon={
                                <CalendarMonth
                                    htmlColor={
                                        theme.palette.other.secondary_light
                                    }
                                />
                            }
                        />
                    )}
                    <DataPoint
                        title="College"
                        value={
                            currentCandidate?.education?.[0]?.college?.value ??
                            ''
                        }
                        icon={
                            <Apartment
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Graduation Date"
                        value={
                            currentCandidate?.education?.[0]?.graduationDate ??
                            ''
                        }
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Container>
            )
        default:
            return (
                <Container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: getPxAsRem(8),
                        flexWrap: 'wrap',
                        padding: getPxAsRem(8),
                    }}
                >
                    <DataPoint
                        title="Company"
                        value={currentWorkExperience?.company?.value ?? ''}
                        icon={
                            <Apartment
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Industries"
                        value={
                            <IndustryOrSectorList
                                items={currentWorkExperience?.industries || []}
                                limit={2}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'ellipsis',
                                    maxWidth: getPxAsRem(150),
                                }}
                            />
                        }
                        icon={
                            <Work
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Sectors"
                        value={
                            <IndustryOrSectorList
                                items={currentWorkExperience?.sectors || []}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'ellipsis',
                                    maxWidth: getPxAsRem(150),
                                }}
                                limit={2}
                            />
                        }
                        icon={
                            <Store
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Title"
                        value={currentWorkExperience?.title?.value ?? ''}
                        icon={
                            <Badge
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Office Location"
                        value={
                            currentWorkExperience?.companyOffice?.officeLocation
                                ?.value ?? ''
                        }
                        icon={
                            <LocationOn
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                    <DataPoint
                        title="Start Date"
                        value={currentWorkExperience?.startDate ?? ''}
                        icon={
                            <CalendarMonth
                                htmlColor={theme.palette.other.secondary_light}
                            />
                        }
                    />
                </Container>
            )
    }
}

export default CandidateDetailDataPoints
